import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rhm, apiError, money, dOnly, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, Textarea, statusTone } from "../ui";

const EMPTY = {
    business_name: "", contact_person: "", phone: "", email: "", address: "", gst: "", license_no: "",
    payment_terms: "", credit_limit: 0, outstanding: 0, status: "new", notes: "", assigned_to: "",
};
const STATUSES = ["new", "active", "follow_up", "on_hold", "inactive"];

export default function RhmDealers() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [form, setForm] = useState(null);
    const [order, setOrder] = useState(null);

    const { data: dealers = [], isLoading } = useQuery({
        queryKey: ["rhm-dealers"],
        queryFn: async () => (await rhm.get("/dealers")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["rhm-products"],
        queryFn: async () => (await rhm.get("/products")).data,
    });

    const invalidate = () => qc.invalidateQueries({ queryKey: ["rhm-dealers"] });

    const save = useMutation({
        mutationFn: async (p) => {
            const body = { ...p, credit_limit: Number(p.credit_limit || 0), outstanding: Number(p.outstanding || 0) };
            return p.id ? (await rhm.patch(`/dealers/${p.id}`, body)).data : (await rhm.post("/dealers", body)).data;
        },
        onSuccess: () => { toast.success("Dealer saved"); setForm(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });

    const placeOrder = useMutation({
        mutationFn: async (p) => (await rhm.post("/dealer-orders", {
            dealer_id: p.dealer_id, order_date: p.order_date || null,
            items: [{ product_id: p.product_id, quantity: Number(p.quantity), unit_price: Number(p.unit_price || 0) }],
        })).data,
        onSuccess: () => { toast.success("Dealer order recorded"); setOrder(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <div data-testid="rhm-dealers-page">
            <PageHead title="Dealers & Distributors" sub="Trade accounts with reorder pattern prediction."
                action={can("dealers.create") && (
                    <Btn onClick={() => setForm(EMPTY)} data-testid="rhm-add-dealer">
                        <Plus className="w-3.5 h-3.5 inline mr-1.5" /> ADD DEALER
                    </Btn>
                )} />

            <Card title={`${dealers.length} dealer(s)`}>
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-dealers-table"
                        head={["Business", "Contact", "Orders", "Value", "Last Order", "Interval", "Next Expected", "Reorder", "Status", ""]}
                        empty={!dealers.length && <Empty msg="No dealers yet." />}>
                        {dealers.map((d) => (
                            <tr key={d.id} data-testid={`rhm-dealer-row-${d.id}`}>
                                <Td className="font-semibold">{d.business_name}</Td>
                                <Td className="text-sm text-zinc-400">{d.contact_person || "—"}<div className="text-xs">{d.phone}</div></Td>
                                <Td>{d.order_count}</Td>
                                <Td>{money(d.total_value)}</Td>
                                <Td className="text-sm">{d.last_order_date ? dOnly(d.last_order_date) : "—"}</Td>
                                <Td className="text-sm text-zinc-400">{d.avg_interval_days ? `${d.avg_interval_days}d` : "—"}</Td>
                                <Td className="text-sm">{d.expected_next_order ? dOnly(d.expected_next_order) : "—"}</Td>
                                <Td><Badge tone={d.reorder_state === "overdue" ? "danger" : d.reorder_state === "due" ? "gold" : "muted"}>
                                    {String(d.reorder_state).toUpperCase()}</Badge></Td>
                                <Td><Badge tone={statusTone(d.status)}>{titleCase(d.status)}</Badge></Td>
                                <Td>
                                    <div className="flex gap-3">
                                        {can("dealers.edit") && (
                                            <button onClick={() => setForm(d)} className="mono text-[10px] tracking-[0.16em] text-gold"
                                                data-testid={`rhm-edit-dealer-${d.id}`}>EDIT</button>
                                        )}
                                        {can("dealers.create") && (
                                            <button onClick={() => setOrder({ dealer_id: d.id, product_id: products[0]?.id || "", quantity: "", unit_price: "", order_date: "" })}
                                                className="mono text-[10px] tracking-[0.16em] text-lime"
                                                data-testid={`rhm-dealer-order-${d.id}`}>+ ORDER</button>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={!!form} onClose={() => setForm(null)} wide testId="rhm-dealer-modal"
                title={form?.id ? "Edit Dealer" : "Add Dealer"}>
                {form && (
                    <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-4">
                        <div className="grid md:grid-cols-3 gap-4">
                            <Field label="Business name" required><Input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="dealer-name" /></Field>
                            <Field label="Contact person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
                            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="dealer-phone" /></Field>
                            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                            <Field label="GST"><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></Field>
                            <Field label="Drug licence no."><Input value={form.license_no} onChange={(e) => setForm({ ...form, license_no: e.target.value })} /></Field>
                            <Field label="Payment terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></Field>
                            <Field label="Credit limit (₹)"><Input type="number" min="0" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} /></Field>
                            <Field label="Outstanding (₹)"><Input type="number" value={form.outstanding} onChange={(e) => setForm({ ...form, outstanding: e.target.value })} /></Field>
                            <Field label="Status"><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUSES} data-testid="dealer-status" /></Field>
                            <Field label="Assigned manager"><Input value={form.assigned_to || ""} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} /></Field>
                            <Field label="Address" className="md:col-span-3"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                            <Field label="Notes" className="md:col-span-3"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                        </div>
                        <div className="flex gap-3">
                            <Btn type="submit" disabled={save.isPending} data-testid="dealer-submit">SAVE DEALER</Btn>
                            <Btn type="button" variant="ghost" onClick={() => setForm(null)}>CANCEL</Btn>
                        </div>
                    </form>
                )}
            </Modal>

            <Modal open={!!order} onClose={() => setOrder(null)} title="Record Dealer Order" testId="rhm-dealer-order-modal">
                {order && (
                    <form onSubmit={(e) => { e.preventDefault(); placeOrder.mutate(order); }} className="space-y-4">
                        <Field label="Product" required>
                            <Select required value={order.product_id} onChange={(e) => setOrder({ ...order, product_id: e.target.value })} data-testid="dealer-order-product">
                                <option value="">Select</option>
                                {products.map((p) => <option key={p.id} value={p.id}>{p.product_code} · {p.name}</option>)}
                            </Select>
                        </Field>
                        <div className="grid md:grid-cols-3 gap-4">
                            <Field label="Quantity" required><Input type="number" min="1" required value={order.quantity} onChange={(e) => setOrder({ ...order, quantity: e.target.value })} data-testid="dealer-order-qty" /></Field>
                            <Field label="Unit price (₹)"><Input type="number" min="0" value={order.unit_price} onChange={(e) => setOrder({ ...order, unit_price: e.target.value })} data-testid="dealer-order-price" /></Field>
                            <Field label="Order date"><Input type="date" value={order.order_date} onChange={(e) => setOrder({ ...order, order_date: e.target.value })} data-testid="dealer-order-date" /></Field>
                        </div>
                        <Btn type="submit" disabled={placeOrder.isPending} data-testid="dealer-order-submit">RECORD ORDER</Btn>
                    </form>
                )}
            </Modal>
        </div>
    );
}
